import { FC, MouseEvent, ReactNode } from "react";
import { CircularProgress } from "../CircularProgress";
import { Icon, type IconName } from "../Icons";
import { Tooltip } from "../Tooltip";
import "./IconButton.css";

/**
 * Available icon button sizes
 */
export type IconButtonSize = "sm" | "md" | "lg";

/**
 * Available icon button variants
 */
export type IconButtonVariant =
  | "default"
  | "primary"
  | "secondary"
  | "danger"
  | "ghost";

/**
 * Props for the IconButton component
 */
export interface IconButtonProps {
  /**
   * Name of the icon to display from the icon registry
   */
  iconName?: IconName;

  /**
   * Custom icon component to display (takes precedence over iconName)
   */
  icon?: ReactNode;

  /**
   * SVG icon to display (takes precedence over iconName and icon)
   */
  svgIcon?: React.FC<React.SVGProps<SVGSVGElement>>;

  /**
   * Visual variant of the button
   * @default "default"
   */
  variant?: IconButtonVariant;

  /**
   * Size of the button
   * @default "md"
   */
  size?: IconButtonSize;

  /**
   * Whether the button is disabled
   * @default false
   */
  disabled?: boolean;

  /**
   * Whether the button is in loading state
   * @default false
   */
  isLoading?: boolean;

  /**
   * Progress value between 0 and 100 when in loading state
   * When provided, displays progress circle instead of animated spinner
   */
  progress?: number;

  /**
   * Tooltip content to display on hover
   */
  tooltip?: string | ReactNode;

  /**
   * Placement of the tooltip
   * @default "bottom"
   */
  tooltipPlacement?: "top" | "bottom" | "left" | "right";

  /**
   * Accessible name for the button. Defaults to `tooltip` when that is a
   * string; set this explicitly when the tooltip is a node, or when the button
   * is labelled by a tooltip on a surrounding wrapper.
   */
  ariaLabel?: string;

  /**
   * Click handler for the button
   */
  onClick?: (e: MouseEvent<HTMLButtonElement>) => void;

  /**
   * Additional CSS class names
   */
  className?: string;

  /**
   * Test ID for testing purposes
   */
  "data-testid"?: string;

  /**
   * Additional children to render after the icon
   */
  children?: ReactNode;
}

/**
 * IconButton Component
 *
 * A versatile button component that displays an icon with optional tooltip and loading state.
 * Supports multiple variants, sizes, and accessibility features.
 *
 * Features:
 * - Multiple visual variants (default, primary, secondary, danger, ghost)
 * - Different sizes (sm, md, lg)
 * - Loading state with spinner
 * - Tooltip support with customizable placement
 * - Disabled state handling
 * - Full accessibility support
 *
 * @example
 * // Basic usage
 * <IconButton iconName="search" onClick={handleSearch} />
 *
 * @example
 * // With custom icon component
 * <IconButton icon={<CustomIcon />} onClick={handleAction} />
 *
 * @example
 * // With tooltip and variant
 * <IconButton
 *   iconName="edit"
 *   variant="primary"
 *   tooltip="Edit item"
 *   onClick={handleEdit}
 * />
 *
 * @example
 * // Loading state with progress
 * <IconButton
 *   iconName="upload"
 *   isLoading={isUploading}
 *   progress={uploadProgress}
 *   disabled={isUploading}
 *   tooltip="Upload file"
 * />
 */
export const IconButton: FC<IconButtonProps> = ({
  iconName,
  icon,
  svgIcon: SvgIcon,
  variant = "default",
  size = "md",
  disabled = false,
  isLoading = false,
  progress,
  tooltip,
  tooltipPlacement = "bottom",
  ariaLabel,
  onClick,
  className = "",
  "data-testid": dataTestId,
  children,
}) => {
  // Construct CSS classes
  const buttonClasses = [
    "indigo-icon-button",
    `indigo-icon-button--${variant}`,
    `indigo-icon-button--${size}`,
    // Every size here (24/32/40px) is under the 44px touch minimum, so the
    // hit area is expanded on coarse pointers while the painted button keeps
    // its density. See `.indigo-touch-target` in design-system.css.
    "indigo-touch-target",
    disabled && "indigo-icon-button--disabled",
    isLoading && "indigo-icon-button--loading",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  // Render the icon or loading state
  const iconSize = size === "sm" ? 16 : size === "md" ? 20 : 28;
  const iconElement = isLoading ? (
    <CircularProgress
      size={iconSize}
      progress={progress !== undefined ? progress : 0.5}
      ariaLabel="Loading"
    />
  ) : icon ? (
    <div className="indigo-icon-button__icon">{icon}</div>
  ) : SvgIcon ? (
    <SvgIcon
      className="indigo-icon-button__icon"
      width={iconSize}
      height={iconSize}
    />
  ) : iconName ? (
    <Icon
      name={iconName}
      sizeX={size === "sm" ? 16 : size === "md" ? 20 : 28}
      className="indigo-icon-button__icon"
    />
  ) : null;

  // Button content
  const buttonContent = (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || isLoading}
      className={buttonClasses}
      data-testid={dataTestId}
      aria-label={
        ariaLabel ?? (typeof tooltip === "string" ? tooltip : undefined)
      }
    >
      {iconElement}
      {children}
    </button>
  );

  // Wrap with tooltip if provided
  if (tooltip) {
    return (
      <Tooltip content={tooltip} placement={tooltipPlacement} offset={[0, 8]}>
        {buttonContent}
      </Tooltip>
    );
  }

  return buttonContent;
};

export default IconButton;
